<script setup lang="ts">
import { computed } from 'vue'
import type { Component } from 'vue'
import { UserPlusIcon, CheckFilledIcon, WarningTriangleIcon, WarningCircleIcon, CloseSmallIcon } from '@ecx/ui/icons'

export type BulkImportToastVariant = 'processing' | 'completed' | 'error' | 'partial'

const props = defineProps<{
  variant: BulkImportToastVariant
  title: string
  description: string
  primaryLabel: string
  closeLabel: string
  icon?: Component
}>()

const emit = defineEmits<{
  (e: 'close'): void
  (e: 'primary'): void
}>()

const isSuccess = computed(() => props.variant === 'completed')
const isError = computed(() => props.variant === 'error')
const isPartial = computed(() => props.variant === 'partial')
const processingIcon = computed(() => props.icon ?? UserPlusIcon)
</script>

<template>
  <div
    :role="isError || isPartial ? 'alert' : 'status'"
    :aria-live="isError || isPartial ? 'assertive' : 'polite'"
    class="w-bulk-import-toast rounded-2xl border border-grey-100 bg-white px-3.5 py-4 flex flex-col gap-5 shadow-bulk-import-toast"
  >
    <div class="flex items-start gap-3">
      <div
        :class="[
          'flex shrink-0 items-center justify-center w-8 h-8 rounded-lg border-2 border-white',
          isSuccess && 'bg-green-25 ring-1 ring-green-300 text-green-600',
          isError && 'bg-yellow-25 ring-1 ring-yellow-300 text-yellow-600',
          isPartial && 'bg-yellow-25 ring-1 ring-yellow-200 text-yellow-400',
          !isSuccess && !isError && !isPartial && 'bg-royal-25 ring-1 ring-royal-100 text-royal-500',
        ]"
      >
        <CheckFilledIcon v-if="isSuccess" class="w-5 h-5" aria-hidden="true" />
        <WarningCircleIcon v-else-if="isPartial" class="w-5 h-5" aria-hidden="true" />
        <WarningTriangleIcon v-else-if="isError" class="w-5 h-5" aria-hidden="true" />
        <component :is="processingIcon" v-else class="w-5 h-5" aria-hidden="true" />
      </div>

      <div class="flex-1 flex flex-col gap-1">
        <p class="font-mont text-xl font-semibold leading-6 text-royal-950">
          {{ title }}
        </p>
        <p class="text-sm leading-relaxed text-grey-600">
          {{ description }}
        </p>
      </div>

      <button
        type="button"
        class="shrink-0 p-2 rounded-lg hover:bg-grey-50 transition-colors text-grey-800"
        :aria-label="closeLabel"
        @click="emit('close')"
      >
        <CloseSmallIcon class="w-5 h-5" aria-hidden="true" />
      </button>
    </div>

    <div class="flex items-center justify-between gap-3">
      <button
        type="button"
        class="h-8 px-3 py-1.5 bg-white border border-grey-200 rounded-lg text-sm text-grey-900 hover:bg-grey-50 transition-colors"
        @click="emit('close')"
      >
        {{ closeLabel }}
      </button>

      <button
        type="button"
        class="h-8 px-3 py-1.5 bg-royal-500 rounded-md text-xs text-white hover:bg-royal-600 transition-colors"
        @click="emit('primary')"
      >
        {{ primaryLabel }}
      </button>
    </div>
  </div>
</template>
